import type { Role } from '@/types'
import { EXTENDED_PORTAL_TYPE_KEYS } from './financeSolicitacoesExtended'

export type FinanceAreaKey = 'COMERCIAL' | 'PJ' | 'GESTAO' | 'PROCESSOS' | 'OUTROS'

export const FINANCE_AREA_META: Record<
  FinanceAreaKey,
  { label: string; description: string }
> = {
  COMERCIAL: {
    label: 'Comercial',
    description: 'Clientes, contratos, notas fiscais e serviços avulsos.',
  },
  PJ: {
    label: 'Profissional PJ',
    description: 'Viagens, freelas, reembolsos e contratos de prestação.',
  },
  GESTAO: {
    label: 'Gestão',
    description: 'Demandas da liderança e operações internas.',
  },
  PROCESSOS: {
    label: 'Processos & Software',
    description: 'Automação, acessos e suporte de sistemas.',
  },
  OUTROS: {
    label: 'Outros',
    description:
      'Demandas comerciais que não se encaixam nas categorias padrão — alinhamento com cliente, proposta ou operação comercial.',
  },
}

export const MVP_PORTAL_TYPE_KEYS = [
  'P1_ENTRADA_CONTRATO',
  'P5_ACESSO',
  'P7_SERVICOS_CLIENTE',
  'C2_SEGUNDA_VIA',
  'C3_NOTA_FISCAL',
  'C6_SERVICOS_CLIENTE',
] as const

export type MvpPortalTypeKey = (typeof MVP_PORTAL_TYPE_KEYS)[number]

export type PortalTypeKey = MvpPortalTypeKey | import('./financeSolicitacoesExtended').ExtendedPortalTypeKey

export const TYPES_WITH_EMPRESA_PREFILL: MvpPortalTypeKey[] = [
  'C2_SEGUNDA_VIA',
  'C3_NOTA_FISCAL',
  'C6_SERVICOS_CLIENTE',
]

export const FINANCE_FILE_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*'

export type FinanceFormField = {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'file'
  required?: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
  multiple?: boolean
}

const commonEmpresaFields: FinanceFormField[] = [
  { name: 'cnpj', label: 'CNPJ', type: 'text', required: true, placeholder: '00.000.000/0000-00' },
  { name: 'razaoSocial', label: 'Razão social', type: 'text', required: true },
]

export const MVP_FORM_FIELDS: Record<MvpPortalTypeKey, FinanceFormField[]> = {
  P1_ENTRADA_CONTRATO: [
    { name: 'destino', label: 'Destino', type: 'text', required: true },
    { name: 'dataIda', label: 'Data de ida', type: 'date', required: true },
    { name: 'dataVolta', label: 'Data de volta', type: 'date', required: true },
    { name: 'motivo', label: 'Motivo da viagem', type: 'textarea', required: true },
    { name: 'hospedagem', label: 'Hospedagem necessária?', type: 'select', options: [
      { value: 'sim', label: 'Sim' },
      { value: 'nao', label: 'Não' },
    ]},
    { name: 'passagens', label: 'Passagens aéreas?', type: 'select', options: [
      { value: 'sim', label: 'Sim' },
      { value: 'nao', label: 'Não' },
    ]},
    { name: 'valorEstimado', label: 'Valor estimado (R$)', type: 'number', required: true },
    { name: 'observacoes', label: 'Observações', type: 'textarea' },
  ],
  P5_ACESSO: [
    { name: 'cnpj', label: 'CNPJ (prestador)', type: 'text', required: true },
    { name: 'gestorResponsavel', label: 'Gestor responsável', type: 'text', required: true },
    { name: 'valor', label: 'Valor da NF (R$)', type: 'number', required: true },
    {
      name: 'nfAnexada',
      label: 'Nota fiscal (PDF ou imagem)',
      type: 'file',
      required: true,
    },
  ],
  P7_SERVICOS_CLIENTE: [
    {
      name: 'categoria',
      label: 'Categoria',
      type: 'select',
      required: true,
      options: [
        { value: 'transporte', label: 'Transporte' },
        { value: 'alimentacao', label: 'Alimentação' },
        { value: 'hospedagem', label: 'Hospedagem' },
        { value: 'material', label: 'Material' },
        { value: 'outros', label: 'Outros' },
      ],
    },
    { name: 'valor', label: 'Valor (R$)', type: 'number', required: true },
    { name: 'descricao', label: 'Descrição', type: 'textarea', required: true },
    {
      name: 'nfReferencia',
      label: 'Nota fiscal (obrigatória)',
      type: 'file',
      required: true,
    },
  ],
  C2_SEGUNDA_VIA: [
    ...commonEmpresaFields,
    { name: 'alteracoes', label: 'Alterações contratuais solicitadas', type: 'textarea', required: true },
    { name: 'vigencia', label: 'Vigência desejada', type: 'date' },
  ],
  C3_NOTA_FISCAL: [
    ...commonEmpresaFields,
    { name: 'mesReferencia', label: 'Mês de referência', type: 'text', required: true, placeholder: 'MM/AAAA' },
    { name: 'motivo', label: 'Motivo do reenvio', type: 'textarea', required: true },
    { name: 'emailEnvio', label: 'E-mail para envio', type: 'text', required: true },
    {
      name: 'anexoSolicitacao',
      label: 'Anexo (solicitação do cliente ou NF original)',
      type: 'file',
      required: true,
    },
  ],
  C6_SERVICOS_CLIENTE: [
    ...commonEmpresaFields,
    { name: 'descricaoServico', label: 'Descrição do serviço', type: 'textarea', required: true },
    { name: 'prazo', label: 'Prazo desejado', type: 'date' },
    { name: 'valor', label: 'Valor (R$)', type: 'number' },
    {
      name: 'anexoAprovacaoCliente',
      label: 'E-mail do cliente aprovando as demandas',
      type: 'file',
      required: true,
    },
  ],
}

export function areasForRole(role: Role): FinanceAreaKey[] {
  switch (role) {
    case 'usuario_agrada':
      return ['PJ', 'GESTAO', 'OUTROS']
    case 'closer':
    case 'bdr':
      return ['COMERCIAL', 'OUTROS']
    case 'financeiro':
    case 'admin':
    case 'manager':
      return ['COMERCIAL', 'PJ', 'GESTAO', 'PROCESSOS', 'OUTROS']
    default:
      return ['COMERCIAL']
  }
}

export function isMvpType(key: string): key is MvpPortalTypeKey {
  return (MVP_PORTAL_TYPE_KEYS as readonly string[]).includes(key)
}

export function isPortalType(key: string): boolean {
  return isMvpType(key) || (EXTENDED_PORTAL_TYPE_KEYS as readonly string[]).includes(key)
}
